import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { quizQuestions, getSovereigntyLevel } from '../data/quizData';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Header from '../components/Header';
import QuizResults from '../components/QuizResults';

const QuizPage = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  
  const { user, saveQuizResult } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/auth');
    }
  }, [user, navigate]);

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const question = quizQuestions[currentQuestion];
  const isLastQuestion = currentQuestion === quizQuestions.length - 1;
  const isFirstQuestion = currentQuestion === 0;
  const hasAnswered = answers[question?.id] !== undefined;

  const handleAnswer = (value) => {
    setAnswers(prev => ({
      ...prev,
      [question.id]: value
    }));
  };

  const handleNext = () => {
    if (isLastQuestion) {
      // Calculate final score
      const totalScore = Object.values(answers).reduce((sum, val) => sum + val, 0);
      setScore(totalScore);
      
      // Save result
      const level = getSovereigntyLevel(totalScore);
      saveQuizResult(totalScore, level.level);
      
      setShowResults(true);
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstQuestion) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleRetake = () => {
    setAnswers({});
    setCurrentQuestion(0);
    setShowResults(false);
    setScore(0);
  };

  if (!user) {
    return null;
  }

  if (showResults) {
    return <QuizResults score={score} onRetake={handleRetake} />;
  }

  // Group questions by category
  const categories = [...new Set(quizQuestions.map(q => q.category))];
  const currentCategory = question?.category;
  const categoryIndex = categories.indexOf(currentCategory);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-stone-950' : 'bg-stone-50'}`}>
      <Header />
      
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Progress Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <span className={`text-sm font-medium ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
              <span className={`text-sm font-medium ${isDark ? 'text-amber-500' : 'text-amber-600'}`}>
                {Math.round(progress)}% Complete
              </span>
            </div>
            <Progress value={progress} className={`h-2 ${isDark ? 'bg-stone-800' : 'bg-stone-200'}`} />
            
            {/* Category pills */}
            <div className="flex flex-wrap gap-2 mt-4">
              {categories.map((cat, idx) => (
                <span
                  key={cat}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    idx === categoryIndex
                      ? 'bg-amber-500 text-stone-950'
                      : idx < categoryIndex
                      ? isDark 
                        ? 'bg-stone-800 text-stone-400' 
                        : 'bg-stone-200 text-stone-500'
                      : isDark
                      ? 'bg-stone-900 text-stone-600'
                      : 'bg-stone-100 text-stone-400'
                  }`}
                >
                  {cat}
                </span>
              ))}
            </div>
          </div>

          {/* Question Card */}
          <div className={`rounded-3xl p-8 border ${isDark ? 'bg-stone-900/50 border-stone-800' : 'bg-white border-stone-200 shadow-lg'}`}>
            {/* Category Label */}
            <div className="flex items-center gap-2 mb-4">
              <div className="h-px w-8 bg-amber-500" />
              <span className="text-amber-500 text-sm font-medium uppercase tracking-wider">
                {question?.category}
              </span>
            </div>

            {/* Question */}
            <h2 className={`text-2xl font-bold mb-8 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
              {question?.question}
            </h2>

            {/* Options */}
            <div className="space-y-3">
              {question?.options.map((option) => {
                const isSelected = answers[question.id] === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(option.value)}
                    className={`w-full text-left p-5 rounded-2xl border transition-all duration-200 ${
                      isSelected
                        ? 'bg-amber-500/10 border-amber-500 ring-2 ring-amber-500/20'
                        : isDark
                        ? 'bg-stone-800/50 border-stone-700 hover:border-stone-600 hover:bg-stone-800'
                        : 'bg-stone-50 border-stone-200 hover:border-stone-300 hover:bg-stone-100'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        isSelected
                          ? 'bg-amber-500 border-amber-500'
                          : isDark
                          ? 'border-stone-600'
                          : 'border-stone-300'
                      }`}>
                        {isSelected && <Check className="w-4 h-4 text-stone-950" />}
                      </div>
                      <span className={`text-lg ${
                        isSelected
                          ? isDark ? 'text-amber-400' : 'text-amber-700'
                          : isDark ? 'text-stone-300' : 'text-stone-700'
                      }`}>
                        {option.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-10 pt-6 border-t border-stone-800">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={isFirstQuestion}
                className={`${
                  isDark 
                    ? 'border-stone-700 text-stone-400 hover:bg-stone-800 disabled:opacity-30' 
                    : 'border-stone-300 text-stone-600 hover:bg-stone-100 disabled:opacity-30'
                }`}
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Previous
              </Button>

              <Button
                onClick={handleNext}
                disabled={!hasAnswered}
                className="bg-amber-500 hover:bg-amber-600 text-stone-950 font-semibold disabled:opacity-50"
              >
                {isLastQuestion ? (
                  <>
                    See Results
                    <Check className="w-5 h-5 ml-2" />
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Hint */}
          <div className={`flex items-start gap-3 mt-6 p-4 rounded-xl ${isDark ? 'bg-stone-900/30' : 'bg-stone-100'}`}>
            <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-stone-500' : 'text-stone-400'}`} />
            <p className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>
              Answer honestly based on your current reality, not where you hope to be. Your results will help identify your next steps toward sovereignty.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizPage;
